// ────────────────────────────────────────────────────────────────────────────
// Per-year budget reconciliation. Pure function — no React, no I/O.
//
// Slice 3: when a year's intended contributions plus mandatory outflows
// exceed the year's after-tax cash inflows, automatically reduce
// contributions in priority HYSA → Taxable → Retirement (mirroring the
// existing `cash_flow` funding-source mechanism in the projection engine).
//
// Convention on `intended.retirement`: this MUST be the after-tax-routed
// retirement contribution (Roth-401k via Roth or mega-backdoor, Roth-IRA,
// after-tax-401k, employer match — anything that consumes take-home pay or
// is a no-cost addition). Pretax 401(k) employee deferrals never reach
// take-home pay (they are W-2 reductions), so they are invisible to this
// reconciliation and MUST NOT be passed in. The projection engine handles
// this split when constructing the input.
// ────────────────────────────────────────────────────────────────────────────

export interface ReconcileBudgetInput {
  /** After-tax cash actually arriving in the user's hands this year. */
  cashIn: number;
  /** Mandatory outflows the user cannot reasonably defer or skip:
   *  household spend, escrow, healthcare/ACA, IRMAA, mortgage P&I, loan P&I. */
  mandatoryOut: number;
  /** Discretionary cash outflows (purchase / gift / home_improvement events
   *  whose funding source was `cash_flow` this year). Bucket-funded outflows
   *  are NOT counted here because they don't draw from take-home cashIn. */
  discretionaryOut: number;
  /** Intended contributions, AFTER per-rule cap enforcement. The reconciler
   *  trims further only when cash is short. */
  intended: {
    retirement: number;
    taxable: number;
    hysa: number;
  };
}

export interface ReconcileBudgetResult {
  /** Actual contributions after auto-reduction (≤ intended in every bucket). */
  actual: { retirement: number; taxable: number; hysa: number };
  /** Per-bucket dollars trimmed (HYSA → Taxable → Retirement priority). */
  autoReduction: {
    fromHysa: number;
    fromTaxable: number;
    fromRetirement: number;
    /** fromHysa + fromTaxable + fromRetirement. */
    total: number;
  };
  /** True iff cash is so short that even after zeroing every bucket the
   *  mandatory + discretionary outflow still exceeds cashIn. */
  overstretched: boolean;
  /** Net cash surplus at year-end:
   *  cashIn − mandatoryOut − discretionaryOut − sum(actual). Negative when
   *  overstretched. */
  netCashSurplus: number;
}

/** Reconcile a year's cash flow against intended contributions. Auto-reduces
 *  in HYSA → Taxable → Retirement priority when intended outflows exceed
 *  cashIn. Mirrors the `cash_flow` funding-source semantics already in the
 *  projection engine; this helper centralizes that logic so a single budget
 *  pass covers both authored cash_flow events and ordinary contributions. */
export function reconcileBudget(input: ReconcileBudgetInput): ReconcileBudgetResult {
  // Treat any negative input as zero — defensive against upstream sign bugs
  // (e.g., a cashIn computation that goes negative because of an over-zealous
  // pretax deferral subtraction).
  const cashIn = Math.max(0, input.cashIn);
  const mandatoryOut = Math.max(0, input.mandatoryOut);
  const discretionaryOut = Math.max(0, input.discretionaryOut);
  const intendedHysa = Math.max(0, input.intended.hysa);
  const intendedTaxable = Math.max(0, input.intended.taxable);
  const intendedRetirement = Math.max(0, input.intended.retirement);

  const availableCash = cashIn - mandatoryOut - discretionaryOut;
  const desiredContribs = intendedHysa + intendedTaxable + intendedRetirement;
  const shortfall = Math.max(0, desiredContribs - availableCash);

  // Overstretched = even zeroing every contribution doesn't balance the
  // year. Equivalent to: shortfall > total intended contributions, i.e.
  // mandatory + discretionary already exceeds cashIn.
  const overstretched = shortfall > desiredContribs;

  if (shortfall === 0) {
    return {
      actual: {
        retirement: intendedRetirement,
        taxable: intendedTaxable,
        hysa: intendedHysa,
      },
      autoReduction: { fromHysa: 0, fromTaxable: 0, fromRetirement: 0, total: 0 },
      overstretched: false,
      netCashSurplus:
        cashIn - mandatoryOut - discretionaryOut - desiredContribs,
    };
  }

  // Apply reductions HYSA → Taxable → Retirement.
  const fromHysa = Math.min(intendedHysa, shortfall);
  const fromTaxable = Math.min(intendedTaxable, shortfall - fromHysa);
  const fromRetirement = Math.min(
    intendedRetirement,
    shortfall - fromHysa - fromTaxable,
  );
  const total = fromHysa + fromTaxable + fromRetirement;

  const actual = {
    hysa: intendedHysa - fromHysa,
    taxable: intendedTaxable - fromTaxable,
    retirement: intendedRetirement - fromRetirement,
  };

  const netCashSurplus =
    cashIn - mandatoryOut - discretionaryOut - (actual.retirement + actual.taxable + actual.hysa);

  return {
    actual,
    autoReduction: { fromHysa, fromTaxable, fromRetirement, total },
    overstretched,
    netCashSurplus,
  };
}
